import React, { useState, useEffect, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';

import MainDashboard from './MainDashboard';
import BoardsList from './BoardsList';
import TaskListDashboard from './task_list/taskListDashboard';
import SchoolContactSearchDashboard from './school_contact_search/schoolContactSearchDashboard';

import Header, { Notification } from './Header';

const App: React.FC = () => {
  const { loginWithRedirect, isAuthenticated, user } = useAuth0();

  /* ---------- page routing (persists to localStorage) ---------- */
  const [page, setPage] = useState<
    'dashboard' | 'boards' | 'tasklist' | 'schoolContactSearch'
  >(() => {
    const stored = localStorage.getItem('appCurrentPage');
    if (
      stored === 'dashboard' ||
      stored === 'boards' ||
      stored === 'tasklist' ||
      stored === 'schoolContactSearch'
    ) {
      return stored;
    }
    return 'dashboard';
  });

  useEffect(() => {
    localStorage.setItem('appCurrentPage', page);
  }, [page]);

  /* ---------- notification centre (task assignments) ---------- */
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unread, setUnread] = useState(0);
  const prevAssigned = useRef<Set<string>>(new Set());
  const initialised = useRef(false);

  const addNotification = (message: string) => {
    setNotifications(prev => [{ id: Date.now().toString(), message }, ...prev].slice(0, 5));
    setUnread(c => c + 1);
  };

  useEffect(() => {
    if (!isAuthenticated || !user) return;

    const uid = (user as any).sub || user.email || '';
    if (!uid) return;

    const es = new EventSource('/api/tasklist/subscribe');
    es.onmessage = e => {
      try {
        const data = JSON.parse(e.data);
        const assigned = new Set<string>();

        (data.sections || []).forEach((sec: any) => {
          (sec.subsections || []).forEach((sub: any) => {
            const subPersonCols = (sub.columns || [])
              .filter((c: any) => c.type === 'person')
              .map((c: any) => c.id);

            (sub.groups || []).forEach((g: any) => {
              const groupPersonCols = (g.columns || [])
                .filter((c: any) => c.type === 'person')
                .map((c: any) => c.id);

              const personCols = [...subPersonCols, ...groupPersonCols];

              (g.tasks || []).forEach((t: any) => {
                const isAssigned = personCols.some(
                  (id: string) => t.extras && t.extras[id] === uid
                );
                if (isAssigned) {
                  assigned.add(t.id);
                  if (initialised.current && !prevAssigned.current.has(t.id)) {
                    addNotification(`Assigned to task "${t.text}"`);
                  }
                }
              });
            });
          });
        });

        prevAssigned.current = assigned;
        if (!initialised.current) initialised.current = true;
      } catch (err) {
        console.error(err);
      }
    };

    return () => es.close();
  }, [isAuthenticated, user]);

  /* ---------- render helpers ---------- */
  const handleOpenNotifications = () => setUnread(0);
  const handleNotificationClick = (_n: Notification) => setPage('tasklist');

  const renderContent = () => {
    if (!isAuthenticated) {
      return (
        <div
          className="flex flex-col items-center mt-10 w-full"
          style={{ backgroundColor: '#fdfdfd', minHeight: '100vh' }}
        >
          <h1 className="text-2xl font-bold mb-2 text-orange-500">Welcome to Prodeo</h1>
          <p className="mb-4">This is a great app.</p>
          <button className="text-blue-600 underline" onClick={() => loginWithRedirect()}>
            Click here to login
          </button>
        </div>
      );
    }

    switch (page) {
      case 'boards':
        return <BoardsList onBack={() => setPage('dashboard')} />;
      case 'tasklist':
        return <TaskListDashboard onBack={() => setPage('dashboard')} />;
      case 'schoolContactSearch':
        return <SchoolContactSearchDashboard onBack={() => setPage('dashboard')} />;
      default:
        return (
          <MainDashboard
            onBoardsClick={() => setPage('boards')}
            onTaskListClick={() => setPage('tasklist')}
            onSchoolContactSearchClick={() => setPage('schoolContactSearch')}
          />
        );
    }
  };

  return (
    <>
      <Header
        notifications={notifications}
        unreadCount={unread}
        onOpen={handleOpenNotifications}
        onNotificationClick={handleNotificationClick}
      />
      {renderContent()}
    </>
  );
};

export default App;
